import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { setCompanyContext } from '@/api/client';
import { setAnalyticsScope } from '@/lib/analytics-api';
import { readStoredSite, storeSite } from './site-storage';

interface CompanyContextValue {
  companyId: string;
  siteId?: string;
  setCompanyId: (id: string) => void;
  setSiteId: (id?: string) => void;
}

const CompanyContext = createContext<CompanyContextValue | null>(null);

export function CompanyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [companyId, setCompanyIdState] = useState('');
  const [siteId, setSiteIdState] = useState<string | undefined>();

  // Initialize from user's custom claims
  useEffect(() => {
    if (!user?.companyId) return;
    const stored = readStoredSite(user.companyId);
    setCompanyIdState(user.companyId);
    setSiteIdState(stored);
    setCompanyContext(user.companyId, stored);
    setAnalyticsScope(user.companyId, stored ?? null);
  }, [user?.companyId]);

  const setCompanyId = (id: string) => {
    const stored = readStoredSite(id);
    setCompanyIdState(id);
    setSiteIdState(stored);
    setCompanyContext(id, stored);
    setAnalyticsScope(id, stored ?? null);
  };

  const setSiteId = (id?: string) => {
    setSiteIdState(id);
    if (companyId) storeSite(companyId, id);
    setCompanyContext(companyId, id);
    setAnalyticsScope(companyId || null, id);
  };

  return (
    <CompanyContext.Provider value={{ companyId, siteId, setCompanyId, setSiteId }}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) throw new Error('useCompany must be used within CompanyProvider');
  return context;
}
